import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { saveManufacturingOverheadConsumption } from "@/lib/manufacturingClient";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;

function formatUsd(value: number): string {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OverheadConsumptionStrip({ state }: { state: PeriodValuesState }) {
  const { summary, plantId, actionBusy, reloadSummary } = state;
  const consumption = summary?.consumption;
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = choice || (consumption?.uses_last_day ? "last" : consumption?.day ? String(consumption.day) : "");

  async function save() {
    if (!plantId || !selected) return;
    setBusy(true);
    setError(null);
    const r = await saveManufacturingOverheadConsumption({
      plant_id: plantId,
      uses_last_day: selected === "last",
      consumption_day: selected === "last" ? null : Number(selected),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setChoice("");
    await reloadSummary();
  }

  if (!summary) return null;
  const recorded = consumption?.recorded_total_usd;
  const previous = consumption?.previous_recorded_total_usd;

  return (
    <div className="mb-4 space-y-2 rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-zinc-300">
          Consumption day
          <select
            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            value={selected}
            onChange={(event) => setChoice(event.target.value)}
            disabled={busy || actionBusy}
          >
            <option value="">Choose…</option>
            {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
              <option key={day} value={day}>{day}</option>
            ))}
            <option value="last">Last day of the month</option>
          </select>
        </label>
        <button type="button" className={ghostBtnClass} disabled={!plantId || !selected || busy || actionBusy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save day"}
        </button>
      </div>
      {consumption?.missing_table ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">Apply docs/sql/mfg_overhead_consumption_v1.sql to record monthly overhead.</p>
      ) : null}
      {error || consumption?.error ? <p className="text-xs text-red-700 dark:text-red-300">{error ?? consumption?.error}</p> : null}
      {consumption?.configured ? (
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          {recorded == null
            ? `Not recorded yet. The next record is ${consumption.upcoming_on ?? "the consumption day"}.`
            : `Recorded ${consumption.recorded_on}: ${formatUsd(recorded)}${previous == null ? "" : ` · previous ${formatUsd(previous)}`}.`}
          {consumption.retired_count > 0 ? ` ${consumption.retired_count} retired item${consumption.retired_count === 1 ? "" : "s"} left out.` : ""}
        </p>
      ) : (
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Pick the day this plant’s overhead is recorded. Until that day, payments and principal stay editable.
        </p>
      )}
      {consumption && consumption.balances.length > 0 ? (
        <details className="text-xs text-gray-700 dark:text-zinc-300">
          <summary className="cursor-pointer text-indigo-700 hover:underline dark:text-indigo-300">
            Consumption details
          </summary>
          <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto">
            {consumption.balances.map((balance) => (
              <li key={balance.bucket_id}>
                {balance.bucket_name}: {formatUsd(balance.remaining_usd)} remaining
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
